import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface SystemMetrics {
  activeUsers: number;
  rateLimitHits: number;
  cspViolations: number;
  edgeFunctionErrors: number;
  avgResponseTime: number;
}

export interface CSPViolation {
  id: string;
  blocked_uri: string;
  violated_directive: string;
  created_at: string;
}

export interface EdgeFunctionPerformance {
  id: string;
  function_name: string;
  execution_time_ms: number;
  status_code: number | null;
  error_message: string | null;
  created_at: string;
}

export interface RateLimitHit {
  id: string;
  event_type: string;
  details: any;
  created_at: string;
}

export const useSystemMonitoring = () => {
  const [metrics, setMetrics] = useState<SystemMetrics>({
    activeUsers: 0,
    rateLimitHits: 0,
    cspViolations: 0,
    edgeFunctionErrors: 0,
    avgResponseTime: 0,
  });
  const [cspViolations, setCSPViolations] = useState<CSPViolation[]>([]);
  const [edgeFunctionPerf, setEdgeFunctionPerf] = useState<EdgeFunctionPerformance[]>([]);
  const [rateLimitHits, setRateLimitHits] = useState<RateLimitHit[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Fetch initial data
  const fetchMetrics = async () => {
    try {
      setLoading(true);

      // Fetch active users (users active in last 5 minutes)
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
      const { count: activeUsersCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .gte('updated_at', fiveMinutesAgo);

      // Fetch CSP violations (last 24 hours)
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data: cspData, count: cspCount } = await supabase
        .from('csp_violations')
        .select('*', { count: 'exact' })
        .gte('created_at', oneDayAgo)
        .order('created_at', { ascending: false })
        .limit(10);

      // Fetch rate limit hits from security_events
      const { data: rateLimitData, count: rateLimitCount } = await supabase
        .from('security_events')
        .select('*', { count: 'exact' })
        .eq('event_type', 'rate_limit_exceeded')
        .gte('created_at', oneDayAgo)
        .order('created_at', { ascending: false })
        .limit(10);

      // Fetch edge function performance
      const { data: perfData, error: perfError } = await supabase
        .from('edge_function_performance')
        .select('*')
        .gte('created_at', oneDayAgo)
        .order('created_at', { ascending: false })
        .limit(50);

      if (perfError) throw perfError;

      // Calculate metrics
      const errorCount = perfData?.filter(p => p.status_code && p.status_code >= 400).length || 0;
      const avgTime = perfData?.length 
        ? Math.round(perfData.reduce((sum, p) => sum + p.execution_time_ms, 0) / perfData.length)
        : 0;

      setMetrics({
        activeUsers: activeUsersCount || 0,
        rateLimitHits: rateLimitCount || 0,
        cspViolations: cspCount || 0,
        edgeFunctionErrors: errorCount,
        avgResponseTime: avgTime,
      });

      setCSPViolations(cspData || []);
      setEdgeFunctionPerf(perfData || []);
      setRateLimitHits(rateLimitData || []);

    } catch (error) {
      console.error('Error fetching monitoring metrics:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch monitoring metrics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();

    // Set up realtime subscriptions
    const cspChannel = supabase
      .channel('csp-violations-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'csp_violations',
        },
        (payload) => {
          console.log('New CSP violation:', payload);
          setCSPViolations(prev => [payload.new as CSPViolation, ...prev.slice(0, 9)]);
          setMetrics(prev => ({ ...prev, cspViolations: prev.cspViolations + 1 }));
        }
      )
      .subscribe();

    const perfChannel = supabase
      .channel('edge-function-perf-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'edge_function_performance',
        },
        (payload) => {
          console.log('New performance metric:', payload);
          const newPerf = payload.new as EdgeFunctionPerformance;
          setEdgeFunctionPerf(prev => [newPerf, ...prev.slice(0, 49)]);
          
          // Update metrics
          setMetrics(prev => {
            const isError = newPerf.status_code && newPerf.status_code >= 400;
            return {
              ...prev,
              edgeFunctionErrors: isError ? prev.edgeFunctionErrors + 1 : prev.edgeFunctionErrors,
            };
          });
        }
      )
      .subscribe();

    // Refresh metrics every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);

    return () => {
      supabase.removeChannel(cspChannel);
      supabase.removeChannel(perfChannel);
      clearInterval(interval);
    };
  }, [toast]);

  return {
    metrics,
    cspViolations,
    edgeFunctionPerf,
    rateLimitHits,
    loading,
    refresh: fetchMetrics,
  };
};
